import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, FileDown, Landmark, LockKeyhole, Building2 } from "lucide-react";
import { scrollToId } from "./Header";
import { Countdown } from "./Countdown";
import { useLang } from "@/i18n/LanguageContext";

const Lattice = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let w, h, raf;
    const mouse = { x: -9999, y: -9999 };
    let nodes = [];

    const build = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      nodes = [];
      const gap = w < 640 ? 110 : 130;
      for (let x = gap / 2; x < w + gap; x += gap) {
        for (let y = gap / 2; y < h + gap; y += gap) {
          nodes.push({
            ox: x + (Math.random() - 0.5) * 40,
            oy: y + (Math.random() - 0.5) * 40,
            ph: Math.random() * Math.PI * 2,
            sp: 0.4 + Math.random() * 0.5,
          });
        }
      }
    };

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };

    let t = 0;
    const draw = () => {
      if (document.hidden) {
        raf = requestAnimationFrame(draw);
        return;
      }
      t += 0.008;
      ctx.clearRect(0, 0, w, h);
      const pts = nodes.map((n) => {
        let x = n.ox + Math.sin(t * n.sp + n.ph) * 14;
        let y = n.oy + Math.cos(t * n.sp + n.ph) * 14;
        const dx = mouse.x - x;
        const dy = mouse.y - y;
        const d = Math.hypot(dx, dy);
        if (d < 180 && d > 0.01) {
          const f = ((180 - d) / 180) * 22;
          x += (dx / d) * f;
          y += (dy / d) * f;
        }
        return { x, y, d };
      });

      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const dist = Math.hypot(dx, dy);
          if (dist < 165) {
            const nearMouse = Math.min(pts[i].d, pts[j].d) < 200;
            ctx.strokeStyle = nearMouse
              ? `rgba(29,78,216,${0.34 * (1 - dist / 165)})`
              : `rgba(100,116,139,${0.14 * (1 - dist / 165)})`;
            ctx.lineWidth = nearMouse ? 1.2 : 1;
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }
      pts.forEach((p) => {
        const active = p.d < 200;
        ctx.fillStyle = active ? "rgba(29,78,216,0.85)" : "rgba(100,116,139,0.4)";
        ctx.beginPath();
        ctx.arc(p.x, p.y, active ? 2.6 : 1.8, 0, Math.PI * 2);
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };

    build();
    draw();
    window.addEventListener("resize", build);
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", build);
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return <canvas ref={ref} className="absolute inset-0 w-full h-full" data-testid="hero-lattice-canvas" />;
};

export const Hero = () => {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const LINES = [
    { text: t("hero.l1"), cls: "text-slate-900" },
    { text: t("hero.l2"), cls: "text-blue-700" },
    { text: t("hero.l3"), cls: "text-slate-900" },
  ];

  const STATS = [
    { icon: Landmark, k: "₹6,003 Cr+", v: t("hero.s1v") },
    { icon: LockKeyhole, k: "NIST PQC", v: t("hero.s2v") },
    { icon: Building2, k: "Govt + Private", v: t("hero.s3v") },
  ];

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-svh flex flex-col justify-center overflow-hidden bg-[#F8FAFC] bg-blueprint pt-[72px]">
      <Lattice />
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_35%,#F8FAFC_88%)]" />

      <motion.div style={{ y: yText, opacity }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr),400px] gap-12 lg:gap-14 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="flex flex-wrap items-center gap-3"
            >
              <span className="inline-flex items-center gap-2 text-[11px] font-mono font-bold uppercase tracking-[0.22em] text-blue-700 bg-white/80 backdrop-blur px-4 py-2 rounded-full border border-blue-200 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 live-dot" />
                {t("hero.badge1")}
              </span>
              <span className="inline-flex items-center gap-2 text-[11px] font-mono font-bold uppercase tracking-[0.22em] text-amber-700 bg-amber-50/90 backdrop-blur px-4 py-2 rounded-full border border-amber-200 shadow-sm">
                {t("hero.badge2")}
              </span>
            </motion.div>

            <h1 data-testid="hero-headline" className="mt-8 fluid-h1 font-extrabold tracking-tight leading-[1.05]">
              {LINES.map((l, i) => (
                <span className="mask-line" key={l.text}>
                  <motion.span
                    className={`block ${l.cls}`}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.3 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {l.text}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              data-testid="hero-subheadline"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.95 }}
              className="mt-7 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed"
            >
              {t("hero.sub")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-cta-button"
                onClick={() => scrollToId("#what-we-do")}
                className="group inline-flex items-center gap-2 text-sm sm:text-base font-bold text-white bg-blue-700 hover:bg-slate-900 px-7 py-4 rounded-full shadow-[0_16px_36px_-12px_rgba(29,78,216,0.55)] transition-all duration-300 hover:-translate-y-0.5"
              >
                {t("hero.cta1")}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                data-testid="hero-whitepaper-button"
                onClick={() => scrollToId("#nqm")}
                className="inline-flex items-center gap-2 text-sm sm:text-base font-bold text-slate-800 bg-white/85 backdrop-blur border border-slate-300 hover:border-blue-600 hover:text-blue-700 px-7 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5"
              >
                <FileDown className="w-4 h-4" />
                {t("hero.cta2")}
              </button>
            </motion.div>
          </div>

          <Countdown />
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-200/90 border border-slate-200 rounded-2xl overflow-hidden max-w-4xl shadow-[0_8px_30px_-12px_rgba(15,23,42,0.1)]"
          data-testid="hero-stats-strip"
        >
          {STATS.map((s) => (
            <div key={s.k} className="bg-white/90 backdrop-blur px-6 py-5 flex items-start gap-3.5">
              <s.icon className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
              <div>
                <p className="font-mono font-bold text-slate-900 text-sm sm:text-base">{s.k}</p>
                <p className="text-xs text-slate-500 mt-1 leading-snug">{s.v}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
